import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MultiSelect } from 'react-multi-select-component';
import CommonHeader from '../../util_components/common_header';
import Button from '../../util_components/button';
import CustomInput from '../../util_components/custom_input';
import CustomSelect from '../../util_components/custom_single_select';
import CustomModal from '../../util_components/custom_modal';
import AccessRolesTable from './AccessRolesTable';
import CmsImageFields from '../sales_team/CmsImageFields';
import HasActiveStudentsNotice from '../sales_team/HasActiveStudentsNotice';
import {
  FIRST_NAME_REQUIRED,
  blocked_by_students,
  first_name_missing,
  full_name,
  name_parts
} from '../sales_team/cms_user_fields';
import { get_api_url } from '../../../utils/urls';
import { api_with_method } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';
import {
  SUPER_USER_ONLY_TIP,
  can_edit_user,
  has_super_user,
  visible_access_types
} from './cms_users_logic';
import './cms_users.css';

const initialForm = {
  first_name: '',
  last_name: '',
  profile_photo: '',
  signature_image: '',
  email: '',
  phone_number: '',
  access_types: [],
  department_uuid: '',
  is_active: 1
};

// access_type is a MySQL SET, so the wire format for both add and edit stays a
// comma-separated string — the multi-select only changes how it is composed.
const to_access_type = (selected) => selected.map((opt) => opt.value).join(',');

const from_access_type = (value) =>
  (value || '')
    .split(',')
    .map((t) => t.trim())
    .filter((t) => !!t)
    .map((t) => ({ value: t, label: t }));

export default function CmsUsers() {
  const access_pages = useSelector((state) => state.home.access_pages);
  // access_type is a comma-separated MySQL SET, so a user can hold SUPER_USER
  // alongside other roles — split and match exact tokens rather than comparing
  // the whole string (same pattern as views/chat_groups/index.tsx:84-91).
  const access_type = useSelector((state) => state.home.access_type);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [accessTypes, setAccessTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedUuid, setSelectedUuid] = useState('');
  const [formMode, setFormMode] = useState('');
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, set_uploading] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [blocked, setBlocked] = useState(null);

  const [rolesOpen, setRolesOpen] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [allPages, setAllPages] = useState([]);
  const [editingRole, setEditingRole] = useState('');
  const [rolePages, setRolePages] = useState([]);
  const [customPage, setCustomPage] = useState('');
  const [createRoleOpen, setCreateRoleOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRolePages, setNewRolePages] = useState([]);

  // access driven by cms_page_access rows (SUPER_USER / HR) via get_accessible_pages
  const has_access = (access_pages || []).includes('/cms-users');

  // Role / page-access management is SUPER_USER only — stricter than the
  // add/edit user flows, which HR also holds. The backend enforces the same
  // (validateCMSSuperUser in api_cms_roles.go); this only hides the controls.
  const is_super_user = has_super_user(access_type);

  // access types come from the cms_user.access_type SET definition (served by
  // the list endpoint), so the dropdown always matches what the column accepts
  const access_type_options = visible_access_types(accessTypes, is_super_user).map((opt) => ({
    value: opt,
    label: opt
  }));

  const department_options = departments.map((d) => ({ value: d.uuid, label: d.name }));

  const page_options = allPages.map((p) => ({ value: p, label: p }));

  const notify = (type, message) =>
    dispatch(actions.set_notification_variable(true, type, message));

  const error_message = (e) => {
    try {
      return e.response.data.message || e.response.data.reason;
    } catch (err) {
      return 'Some error occured. Please contact dev team.';
    }
  };

  const load_users = () => {
    setLoading(true);
    const url = get_api_url('/v2/cms_user/manage/users', false, true);
    return api_with_method('get', url, null, true)
      .then((res) => {
        const loaded_users = res.data.data.users || [];
        setUsers(loaded_users);
        setDepartments(res.data.data.departments || []);
        setAccessTypes(res.data.data.access_types || []);
        setLoading(false);
        return loaded_users;
      })
      .catch((e) => {
        notify('error', error_message(e));
        setLoading(false);
        return [];
      });
  };

  useEffect(() => {
    if (has_access) load_users();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close_form = () => {
    setBlocked(null);
    setFormMode('');
    setSelectedUuid('');
    setForm(initialForm);
    set_uploading(false);
  };

  const open_add = () => {
    setFormKey((k) => k + 1);
    setSelectedUuid('');
    setForm(initialForm);
    setFormMode('add');
  };

  const open_edit = (user) => {
    setFormKey((k) => k + 1);
    setSelectedUuid(user.uuid);
    setBlocked(null);
    setForm({
      ...name_parts(user),
      profile_photo: user.profile_photo || '',
      signature_image: user.signature_image || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      access_types: from_access_type(user.access_type),
      department_uuid: user.department_uuid || '',
      is_active: user.is_active
    });
    setFormMode('edit');
  };

  const on_form_change = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const on_access_types_change = (selected) => setForm({ ...form, access_types: selected });

  const submit_add = () => {
    if (uploading) return;
    const { first_name, last_name, profile_photo, signature_image } = form;
    const { email, access_types, department_uuid } = form;
    if (!first_name.trim() || !email || access_types.length === 0) {
      notify('error', 'First name, email and access type are required.');
      return;
    }
    setSubmitting(true);
    // Password is set server-side (random UUID) — sign-in is via Google.
    const url = get_api_url('/v2/cms_user/manage/users', false, true);
    const payload = {
      name: full_name(first_name, last_name),
      first_name: first_name.trim(),
      last_name: last_name.trim(),
      profile_photo,
      signature_image,
      email,
      access_type: to_access_type(access_types),
      department_uuid
    };
    api_with_method('post', url, payload, true)
      .then(() => load_users())
      .then(() => {
        notify('success', 'User added successfully.');
        setSubmitting(false);
        close_form();
      })
      .catch((e) => {
        notify('error', error_message(e));
        setSubmitting(false);
      });
  };

  const submit_edit = () => {
    if (uploading) return;
    if (!form.first_name.trim() || form.access_types.length === 0) {
      notify('error', 'First name and access type are required.');
      return;
    }
    setSubmitting(true);
    setBlocked(null);
    const url = get_api_url(`/v2/cms_user/manage/users/${selectedUuid}`, false, true);
    const payload = {
      name: full_name(form.first_name, form.last_name),
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      profile_photo: form.profile_photo,
      signature_image: form.signature_image,
      phone_number: form.phone_number,
      access_type: to_access_type(form.access_types),
      department_uuid: form.department_uuid,
      is_active: form.is_active
    };
    api_with_method('put', url, payload, true)
      .then(() => {
        notify('success', 'User updated successfully.');
        setSubmitting(false);
        close_form();
        load_users();
      })
      .catch((e) => {
        const refused = blocked_by_students(e, selectedUuid);
        if (refused) setBlocked(refused);
        else notify('error', error_message(e));
        setSubmitting(false);
      });
  };

  const load_roles = () => {
    setRolesLoading(true);
    const url = get_api_url('/v2/cms_user/manage/roles', false, true);
    return api_with_method('get', url, null, true)
      .then((res) => {
        setRoles(res.data.data.roles || []);
        setAllPages(res.data.data.all_pages || []);
        setRolesLoading(false);
      })
      .catch((e) => {
        notify('error', error_message(e));
        setRolesLoading(false);
      });
  };

  const open_roles = () => {
    setRolesOpen(true);
    setEditingRole('');
    setCreateRoleOpen(false);
    load_roles();
  };

  const close_roles = () => {
    setRolesOpen(false);
    setEditingRole('');
    setCreateRoleOpen(false);
    setCustomPage('');
  };

  const open_role_editor = (role) => {
    setEditingRole(role.access_type);
    setRolePages((role.pages || []).map((p) => ({ value: p, label: p })));
    setCustomPage('');
  };

  // A page path that no role holds yet is not in the catalog (which is built
  // from the cms_page_access rows themselves), so allow typing one in.
  const add_custom_page = (selected, setSelected) => {
    const path = customPage.trim();
    if (!path) return;
    if (path[0] !== '/') {
      notify('error', 'Page path must start with "/".');
      return;
    }
    if (!allPages.includes(path)) setAllPages([...allPages, path].sort());
    if (!selected.some((opt) => opt.value === path)) {
      setSelected([...selected, { value: path, label: path }]);
    }
    setCustomPage('');
  };

  const submit_role_pages = () => {
    setSubmitting(true);
    const url = get_api_url(`/v2/cms_user/manage/roles/${editingRole}/pages`, false, true);
    api_with_method('put', url, { pages: rolePages.map((p) => p.value) }, true)
      .then(() => {
        notify('success', `Pages updated for ${editingRole}.`);
        setSubmitting(false);
        setEditingRole('');
        load_roles();
      })
      .catch((e) => {
        notify('error', error_message(e));
        setSubmitting(false);
      });
  };

  const submit_create_role = () => {
    const name = newRoleName.trim().toUpperCase();
    if (!name) {
      notify('error', 'Role name is required.');
      return;
    }
    setSubmitting(true);
    const url = get_api_url('/v2/cms_user/manage/roles', false, true);
    const payload = { access_type: name, pages: newRolePages.map((p) => p.value) };
    api_with_method('post', url, payload, true)
      .then(() => {
        notify('success', `Role ${name} created.`);
        setSubmitting(false);
        setCreateRoleOpen(false);
        setNewRoleName('');
        setNewRolePages([]);
        load_roles();
        // the new member is now part of the access_type SET — refresh the
        // options the user form offers
        load_users();
      })
      .catch((e) => {
        notify('error', error_message(e));
        setSubmitting(false);
      });
  };

  const filtered_users = users.filter((u) => {
    // Exact match on purpose, NOT hasAccess: this hides operators who have no access at all.
    // 'NO_ACCESS,CARE' is a CARE user and belongs in the list, so only a value that is nothing
    // but NO_ACCESS is filtered out.
    if ((u.access_type || '').trim() === 'NO_ACCESS') return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.access_type || '').toLowerCase().includes(q) ||
      (u.department_name || '').toLowerCase().includes(q)
    );
  });

  const render_list = () => (
    <div className="cms-card flex-1 min-w-0 rounded-xl overflow-hidden p-5">
      <div className="cms-list-header flex items-center gap-3 pb-4">
        {/* Fixed width, not w-full: as a flex item w-full made the search box eat
            the whole row and squeeze the buttons until their labels wrapped. */}
        <input
          className="cms-search-input rounded px-3 py-1.5 w-72 max-w-full text-sm"
          placeholder="Search by name, email, role or department"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="cms-users-count text-sm whitespace-nowrap">
          {filtered_users.length} users
        </div>
        <div className="flex items-center gap-3 ml-auto shrink-0">
          <Button custom_style={{ whiteSpace: 'nowrap' }} onClick={open_add}>
            Add User
          </Button>
          {is_super_user ? (
            <Button type="secondary" custom_style={{ whiteSpace: 'nowrap' }} onClick={open_roles}>
              Access Roles
            </Button>
          ) : null}
        </div>
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
        <table className="cms-users-table w-full text-sm">
          <thead>
            <tr>
              <th className="p-3 font-medium text-left">Name</th>
              <th className="p-3 font-medium text-left">Email</th>
              <th className="p-3 font-medium text-left">Role</th>
              <th className="p-3 font-medium text-left">Department</th>
              <th className="p-3 font-medium text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered_users.map((user) => (
              <tr key={user.uuid} className={user.uuid === selectedUuid ? 'is-selected' : ''}>
                <td className="p-3 text-left">{user.name}</td>
                <td className="p-3 text-left break-all">{user.email}</td>
                <td className="p-3 text-left">{user.access_type}</td>
                <td className="p-3 text-left">{user.department_name || '-'}</td>
                <td className="p-3 text-left">
                  <button
                    type="button"
                    data-tooltip-id="my-tooltip"
                    data-tooltip-content={
                      can_edit_user(user, is_super_user) ? 'Edit user' : SUPER_USER_ONLY_TIP
                    }
                    title={can_edit_user(user, is_super_user) ? undefined : SUPER_USER_ONLY_TIP}
                    disabled={!can_edit_user(user, is_super_user)}
                    className="cms-edit-btn cursor-pointer"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                      padding: 0,
                      margin: 0,
                      opacity: can_edit_user(user, is_super_user) ? 1 : 0.4,
                      cursor: can_edit_user(user, is_super_user) ? 'pointer' : 'not-allowed'
                    }}
                    onClick={() => open_edit(user)}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M16.5 3.5a2.121 2.121 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
            {filtered_users.length === 0 && !loading ? (
              <tr>
                <td className="cms-empty p-4 text-left" colSpan={5}>
                  No users found
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );

  // overflow visible (the shared modal body defaults to auto): the access-type
  // dropdown is absolutely positioned, and this form is short enough that the
  // modal box ends just below the fields — the open panel was being clipped to
  // its first option. The form itself never needs to scroll (four fields) and
  // the panel caps its own height in cms_users.css.
  const user_modal_style = { width: '90%', maxWidth: '900px', overflow: 'visible' };

  const render_user_modal = () => {
    const is_edit = formMode === 'edit';
    const submit_label = is_edit ? 'Save' : 'Add User';
    const no_first_name = first_name_missing(form.first_name);
    // Closing mid-upload would let the late result land in the next form.
    const guarded_close = uploading ? () => {} : close_form;
    return (
      <CustomModal
        show={!!formMode}
        close={guarded_close}
        title={is_edit ? 'Edit User' : 'Add User'}
        inner_style={user_modal_style}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <CustomInput
              label="First Name *"
              name="first_name"
              value={form.first_name}
              onChange={on_form_change('first_name')}
              style={{ width: '100%' }}
            />
            {no_first_name ? (
              <div className="text-xs text-state-error" data-testid="first-name-required">
                {FIRST_NAME_REQUIRED}
              </div>
            ) : null}
          </div>
          <div>
            <CustomInput
              label="Last Name"
              name="last_name"
              value={form.last_name}
              onChange={on_form_change('last_name')}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <CustomInput
              label="Email"
              name="email"
              type="email"
              value={form.email}
              disabled={is_edit}
              onChange={on_form_change('email')}
              style={{ width: '100%' }}
            />
          </div>
          <div className="cms-multiselect">
            {/* Label markup mirrors CustomInput / CustomSelect (14px, trailing
                colon) and the control mirrors their 30px height + 10px vertical
                margin, so the two grid columns share a baseline. */}
            <div className="cms-field-label">Access Type:</div>
            <MultiSelect
              options={access_type_options}
              value={form.access_types}
              onChange={on_access_types_change}
              labelledBy="Access Type"
              hasSelectAll={false}
              overrideStrings={{ selectSomeItems: 'Select access types' }}
            />
          </div>
          <div>
            <CustomSelect
              label="Department"
              name="department_uuid"
              value={form.department_uuid}
              options={department_options}
              onChange={on_form_change('department_uuid')}
              defaultOption="Select department (optional)"
              style={{ width: '100%' }}
            />
          </div>
          <CmsImageFields
            key={formKey}
            profile_photo={form.profile_photo}
            signature_image={form.signature_image}
            on_change={(key, url) => setForm((f) => ({ ...f, [key]: url }))}
            notify={notify}
            on_uploading={set_uploading}
          />
        </div>
        {blocked ? <HasActiveStudentsNotice blocked={blocked} /> : null}
        <div className="flex gap-3 mt-6">
          <Button type="secondary" onClick={guarded_close} disabled={submitting || uploading}>
            Cancel
          </Button>
          <Button
            onClick={is_edit ? submit_edit : submit_add}
            disabled={submitting || uploading || no_first_name}
          >
            {submitting ? 'Submitting...' : submit_label}
          </Button>
        </div>
      </CustomModal>
    );
  };

  const render_role_editor = () => (
    <div className="cms-role-editor rounded-lg p-4 mb-4">
      <div className="text-base font-semibold mb-3">Pages for {editingRole}</div>
      <div className="cms-multiselect mb-3">
        <MultiSelect
          options={page_options}
          value={rolePages}
          onChange={setRolePages}
          labelledBy="Pages"
          overrideStrings={{ selectSomeItems: 'Select pages' }}
        />
      </div>
      <div className="flex items-end gap-3 mb-4">
        <CustomInput
          label="Add a page path not in the list (e.g. /new-page)"
          name="custom_page"
          value={customPage}
          onChange={(e) => setCustomPage(e.target.value)}
          style={{ width: '100%' }}
        />
        <Button type="secondary" onClick={() => add_custom_page(rolePages, setRolePages)}>
          Add Path
        </Button>
      </div>
      <div className="flex gap-3">
        <Button type="secondary" onClick={() => setEditingRole('')} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={submit_role_pages} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save Pages'}
        </Button>
      </div>
    </div>
  );

  const render_create_role = () => (
    <div className="cms-role-editor rounded-lg p-4 mb-4">
      <div className="text-base font-semibold mb-3">Create Role</div>
      <div className="mb-3">
        <CustomInput
          label="Role name (e.g. SALES_LEAD)"
          name="new_role_name"
          value={newRoleName}
          onChange={(e) => setNewRoleName(e.target.value)}
          style={{ width: '100%' }}
        />
        <div className="cms-role-hint text-xs mt-1">
          Uppercase letters, digits and underscore only. This adds a member to the
          cms_user.access_type SET column and cannot be undone from the CMS.
        </div>
      </div>
      <div className="cms-multiselect mb-4">
        <div className="cms-field-label">Pages:</div>
        <MultiSelect
          options={page_options}
          value={newRolePages}
          onChange={setNewRolePages}
          labelledBy="Pages"
          overrideStrings={{ selectSomeItems: 'Select pages' }}
        />
      </div>
      <div className="flex gap-3">
        <Button type="secondary" onClick={() => setCreateRoleOpen(false)} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={submit_create_role} disabled={submitting}>
          {submitting ? 'Creating...' : 'Create Role'}
        </Button>
      </div>
    </div>
  );

  const render_roles_modal = () => (
    <CustomModal
      show={rolesOpen}
      close={close_roles}
      title="Access Roles"
      inner_style={{ width: '90%', maxWidth: '1100px' }}
    >
      <div className="flex justify-end mb-4">
        {!createRoleOpen && !editingRole ? (
          <Button onClick={() => setCreateRoleOpen(true)}>Create Role</Button>
        ) : null}
      </div>
      {createRoleOpen ? render_create_role() : null}
      {editingRole ? render_role_editor() : null}
      <AccessRolesTable roles={roles} loading={rolesLoading} on_edit={open_role_editor} />
    </CustomModal>
  );

  if (!has_access) {
    return (
      <CommonHeader title="CMS Users" class_name="cms-users-page">
        <div className="cms-no-access p-4">You do not have access to this page.</div>
      </CommonHeader>
    );
  }

  return (
    <CommonHeader loading={loading} title="CMS Users" class_name="cms-users-page">
      {render_list()}
      {render_user_modal()}
      {rolesOpen ? render_roles_modal() : null}
    </CommonHeader>
  );
}
